import type { Identifier } from "ra-core";

/**
 * Notes the team leaves on a client ("le interesa pero lo habla con su hijo,
 * llamar el lunes"). They live in contact_notes, mixed with the notes the CRM
 * writes by itself (new lead, no answer, Stripe, Packlink...), so these
 * helpers pick out the ones written by a person.
 */

export interface ClientNote {
  id: Identifier;
  contact_id: Identifier;
  sales_id?: Identifier | null;
  text?: string | null;
  date: string;
}

/** team = written by someone; history = the old Excel follow-up; auto = CRM. */
export type NoteKind = "team" | "history" | "auto";

const HISTORY_PREFIX = "Seguimiento del Excel";

// Start of every note the CRM writes by itself
const AUTO_PREFIXES = [
  "Nuevo lead",
  "El contacto ha vuelto a dejar sus datos",
  "Llamada sin respuesta",
  "Compra en la web",
  "Envío creado en Packlink",
  "Envío de Packlink",
  "Packlink:",
  "Incidencia en el envío",
  "Aviso de instalación enviado",
  "Requisitos de instalación estándar",
  "Dirección de envío confirmada",
  "Ojo: en Stripe",
  "Fotos para validar la instalación",
  "Foto recibida por WhatsApp",
  "Pedido #",
  "Seguimiento del envío mandado",
];

export const noteKind = (text?: string | null): NoteKind => {
  const value = (text ?? "").trim();
  if (!value) return "auto";
  if (value.startsWith(HISTORY_PREFIX)) return "history";
  return AUTO_PREFIXES.some((prefix) => value.startsWith(prefix))
    ? "auto"
    : "team";
};

/** The Excel follow-up without its title line. */
export const historyBody = (text?: string | null) =>
  (text ?? "").replace(/^Seguimiento del Excel[^\n]*\n?/, "").trim();

export interface ContactNotes {
  /** Written by the team, newest first */
  team: ClientNote[];
  /** Old Excel follow-up (before the CRM), newest first */
  history: ClientNote[];
}

const EMPTY: ContactNotes = { team: [], history: [] };

/** Team and Excel notes per contact (the CRM's own notes are left out). */
export const groupNotes = (notes: ClientNote[]) => {
  const byContact = new Map<string, ContactNotes>();
  const sorted = [...notes].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
  for (const note of sorted) {
    const kind = noteKind(note.text);
    if (kind === "auto") continue;
    const key = String(note.contact_id);
    const entry = byContact.get(key) ?? { team: [], history: [] };
    entry[kind].push(note);
    byContact.set(key, entry);
  }
  return (contactId?: Identifier | null): ContactNotes =>
    contactId == null ? EMPTY : (byContact.get(String(contactId)) ?? EMPTY);
};

/** "ahora", "hace 5 min", "hace 2 h", "ayer", "hace 3 d", "12 oct". */
export const noteAgo = (date: string, now = new Date()) => {
  const then = new Date(date);
  const minutes = Math.max(
    0,
    Math.round((now.getTime() - then.getTime()) / 60000),
  );
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  if (days === 1) return "ayer";
  if (days < 7) return `hace ${days} d`;
  return then.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
};
